/**
 * Pure presentation rules of the CAN consumption dashboard.
 *
 * Everything here is deliberately free of React so it can be tested on its own. Three rules from the
 * BCF-735 measurements drive most of the code:
 *
 * 1. `null` means "no basis of calculation" and renders as a dash. A zero on a consumption panel
 *    reads as "burned nothing", which is a different — and wrong — statement.
 * 2. A vehicle with a handful of travels leads any consumption ranking. Below `MIN_TRAVELS_FOR_RANKING`
 *    the vehicle still shows in the table, but without color, out of the ranking and out of the
 *    alert count.
 * 3. Kilometers per liter is the one indicator where HIGHER is better. Its color scale, gauge bands
 *    and ranking order are all inverted with respect to every other indicator.
 */

import { CanConsumptionMessages, VehicleTypesMessages } from "@shared/languages/interfaces";
import { CanConsumptionDashboardDay, CanConsumptionDashboardVehicle } from "./can-consumption.types";

/** Rendered whenever a value has no basis of calculation. */
export const NO_VALUE = "—";

/**
 * Minimum number of travels for a vehicle to enter the ranking, the threshold baseline and the
 * alert count. Kept as a constant so it can be promoted to a system parameter later.
 */
export const MIN_TRAVELS_FOR_RANKING = 3;

/** Metrics that carry alert limits, colors and a ranking. */
export type RankedMetricKey = "litersPerM3" | "kmPerLiter" | "litersPerTravel" | "litersPerHour" | "idleLitersPerHour";

export interface MetricDescriptor {
  key: RankedMetricKey;
  /** `true` only for km/L: on every other indicator a bigger number is worse */
  higherIsBetter: boolean;
  fractionDigits: number;
  /** Slider granularity in the alert-limits modal */
  step: number;
  labelKey: CanConsumptionMessages;
  shortLabelKey: CanConsumptionMessages;
  unitKey: CanConsumptionMessages;
}

export const RANKED_METRICS: MetricDescriptor[] = [
  {
    key: "litersPerM3",
    higherIsBetter: false,
    fractionDigits: 2,
    step: 0.05,
    labelKey: CanConsumptionMessages.metricLitersPerM3Label,
    shortLabelKey: CanConsumptionMessages.metricLitersPerM3Short,
    unitKey: CanConsumptionMessages.metricLitersPerM3Unit
  },
  {
    key: "kmPerLiter",
    higherIsBetter: true,
    fractionDigits: 2,
    step: 0.05,
    labelKey: CanConsumptionMessages.metricKmPerLiterLabel,
    shortLabelKey: CanConsumptionMessages.metricKmPerLiterShort,
    unitKey: CanConsumptionMessages.metricKmPerLiterUnit
  },
  {
    key: "litersPerTravel",
    higherIsBetter: false,
    fractionDigits: 1,
    step: 0.5,
    labelKey: CanConsumptionMessages.metricLitersPerTravelLabel,
    shortLabelKey: CanConsumptionMessages.metricLitersPerTravelShort,
    unitKey: CanConsumptionMessages.metricLitersPerTravelUnit
  },
  {
    key: "litersPerHour",
    higherIsBetter: false,
    fractionDigits: 2,
    step: 0.05,
    labelKey: CanConsumptionMessages.metricLitersPerHourLabel,
    shortLabelKey: CanConsumptionMessages.metricLitersPerHourShort,
    unitKey: CanConsumptionMessages.metricLitersPerHourUnit
  },
  {
    key: "idleLitersPerHour",
    higherIsBetter: false,
    fractionDigits: 2,
    step: 0.05,
    labelKey: CanConsumptionMessages.metricIdleLitersPerHourLabel,
    shortLabelKey: CanConsumptionMessages.metricIdleLitersPerHourShort,
    unitKey: CanConsumptionMessages.metricIdleLitersPerHourUnit
  }
];

/** The four indicators shown as gauges, in the order approved with the customer. */
export const GAUGE_METRIC_KEYS: RankedMetricKey[] = ["litersPerM3", "kmPerLiter", "litersPerTravel", "litersPerHour"];

export const metricByKey = (key: RankedMetricKey): MetricDescriptor => (
  RANKED_METRICS.find((metric) => metric.key === key) ?? RANKED_METRICS[0]
);

export interface MetricThresholds {
  warn: number;
  critical: number;
}

export type ThresholdMap = { [key in RankedMetricKey]: MetricThresholds };

export interface MetricScale {
  /** Fleet median used both as the gauge mark and as the mirror of the "good" bands */
  median: number;
  /** Lower and upper bounds of the sliders and of the gauge domain */
  min: number;
  max: number;
  defaultWarn: number;
  defaultCritical: number;
  /** `false` when no vehicle in the baseline reported this indicator */
  hasSample: boolean;
}

export type ScaleMap = { [key in RankedMetricKey]: MetricScale };

/** Five bands of the table heat map, from best to worst. */
export type HeatShade = "best" | "good" | "neutral" | "warning" | "critical";

export type MetricZone = "ok" | "warning" | "critical";

export const isFiniteNumber = (value: number | null | undefined): value is number => (
  typeof value === "number" && Number.isFinite(value)
);

/** Median of the usable values; `null` when none of them has a basis of calculation. */
export const median = (values: (number | null | undefined)[]): number | null => {
  const usable = values.filter(isFiniteNumber).sort((left, right) => left - right);

  if (usable.length === 0) return null;

  const middle = Math.floor(usable.length / 2);

  return usable.length % 2 === 1 ? usable[middle] : (usable[middle - 1] + usable[middle]) / 2;
};

/**
 * Formats a number with the separators of the active language.
 * @param value Number to format
 * @param fractionDigits Decimal places
 * @param decimalSeparator "." for en-US, "," for pt-BR and es
 */
export const formatNumber = (value: number, fractionDigits: number, decimalSeparator: "." | ","): string => {
  const groupSeparator = decimalSeparator === "," ? "." : ",";
  const [integerPart, fractionPart] = Math.abs(value).toFixed(fractionDigits).split(".");
  const grouped = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator);
  const sign = value < 0 ? "-" : "";

  return fractionPart ? `${sign}${grouped}${decimalSeparator}${fractionPart}` : `${sign}${grouped}`;
};

/**
 * Formats a metric value, rendering a dash when it has no basis of calculation.
 * A missing value must NEVER become a zero: on a consumption panel a zero reads as "burned nothing".
 */
export const formatMetricValue = (
  value: number | null | undefined,
  fractionDigits: number,
  decimalSeparator: "." | ","
): string => (isFiniteNumber(value) ? formatNumber(value, fractionDigits, decimalSeparator) : NO_VALUE);

/**
 * Formats a duration given in minutes as "h:mm".
 * Built from the raw minutes on purpose: formatting an interval with an hour mask silently truncates
 * anything above 24 h, and a fleet average can legitimately cross that line.
 */
export const formatHoursAndMinutes = (minutes: number | null | undefined): string => {
  if (!isFiniteNumber(minutes) || minutes < 0) return NO_VALUE;

  const totalMinutes = Math.round(minutes);
  const hours = Math.floor(totalMinutes / 60);
  const remainder = totalMinutes % 60;

  return `${hours}:${String(remainder).padStart(2, "0")}`;
};

/** Translates one message key of the platform — the `t` of `useTranslation`. */
export type TranslateMessage = (key: string) => string;

/**
 * Translation keys of the vehicle types known to the platform, keyed by `id_vehicle_type` for the
 * singular form and by `${id_vehicle_type}-plural` for the plural one.
 */
const vehicleTypeMessages: { [messageKey: string]: string | undefined } = VehicleTypesMessages;

/**
 * Label of a vehicle type, translated from its uuid.
 *
 * The description that travels with the payload is the raw text of the database — always in
 * Portuguese, whatever the language of the platform. Only the uuid is translatable, so it is the one
 * that drives the label. A type outside the known enum has no message key, and then that description
 * is the best the screen can do: showing the raw uuid would be worse than showing the wrong language.
 *
 * @param idVehicleType `tpe_vehicles_types.id_vehicle_type` (uuid)
 * @param description Description as the API returns it, kept only as the fallback
 * @param form Plural for a category (the filter chips), singular for one vehicle (a table row)
 * @param translate The `t` of `useTranslation`
 * @returns The label, or `null` when there is nothing to show
 */
export const vehicleTypeLabel = (
  idVehicleType: string | null | undefined,
  description: string | null | undefined,
  form: "singular" | "plural",
  translate: TranslateMessage
): string | null => {
  const messageKey = idVehicleType
    ? vehicleTypeMessages[form === "plural" ? `${idVehicleType}-plural` : idVehicleType]
    : undefined;

  return messageKey ? translate(messageKey) : (description || null);
};

/**
 * Says what one selection of the filter kept, for the header line of the panel.
 *
 * A single choice is named. More than one would be a list too long to read on a caption — a fleet
 * can have dozens of plants — so it is counted instead. Nothing selected has nothing to say: the
 * line must not carry a "no filter" note.
 *
 * @param options What the filter kept, already labelled by the drawer
 * @param pluralNoun Plural noun of the thing, already translated
 * @returns The text, or `null` when there is nothing to say
 */
export const describeFilterSelection = (
  options: { label: string }[],
  pluralNoun: string
): string | null => {
  if (options.length === 0) return null;

  return options.length === 1 ? options[0].label : `${options.length} ${pluralNoun.toLowerCase()}`;
};

/** A vehicle only enters the ranking, the color scale and the alert count with enough travels. */
export const qualifiesForRanking = (
  vehicle: CanConsumptionDashboardVehicle,
  minTravels: number = MIN_TRAVELS_FOR_RANKING
): boolean => vehicle.travelsCount >= minTravels;

/** Rounds a threshold to the slider granularity of the indicator. */
const snapToStep = (value: number, step: number): number => Number((Math.round(value / step) * step).toFixed(2));

/**
 * Builds the scale of one indicator: fleet median, slider bounds and the default alert limits.
 * The defaults sit at 15% / 35% away from the median, on the side that is worse for the indicator.
 */
export const buildMetricScale = (descriptor: MetricDescriptor, values: (number | null | undefined)[]): MetricScale => {
  const usable = values.filter(isFiniteNumber);
  const fleetMedian = median(usable) ?? 1;
  const defaultWarn = snapToStep(descriptor.higherIsBetter ? fleetMedian * 0.85 : fleetMedian * 1.15, descriptor.step);
  const defaultCritical = snapToStep(descriptor.higherIsBetter ? fleetMedian * 0.7 : fleetMedian * 1.35, descriptor.step);
  const observedMin = usable.length > 0 ? Math.floor(Math.min(...usable) * 10) / 10 : 0;
  const observedMax = usable.length > 0 ? Math.ceil(Math.max(...usable) * 10) / 10 : Math.ceil(fleetMedian * 2);

  return {
    median: fleetMedian,
    min: Math.max(0, Math.min(observedMin, defaultWarn, defaultCritical)),
    max: Math.max(observedMax, defaultWarn, defaultCritical),
    defaultWarn,
    defaultCritical,
    hasSample: usable.length > 0
  };
};

/** Builds the scale of every ranked indicator from the vehicles that have enough travels. */
export const buildMetricScales = (
  vehicles: CanConsumptionDashboardVehicle[],
  minTravels: number = MIN_TRAVELS_FOR_RANKING
): ScaleMap => {
  const baseline = vehicles.filter((vehicle) => qualifiesForRanking(vehicle, minTravels));

  return RANKED_METRICS.reduce((scales, descriptor) => {
    scales[descriptor.key] = buildMetricScale(descriptor, baseline.map((vehicle) => vehicle[descriptor.key]));

    return scales;
  }, {} as ScaleMap);
};

/** Default limits of every indicator, used by the modal's "restore defaults" action. */
export const defaultThresholds = (scales: ScaleMap): ThresholdMap => RANKED_METRICS.reduce((thresholds, descriptor) => {
  thresholds[descriptor.key] = {
    warn: scales[descriptor.key].defaultWarn,
    critical: scales[descriptor.key].defaultCritical
  };

  return thresholds;
}, {} as ThresholdMap);

/**
 * Keeps the critical limit at least as severe as the warning one.
 * "More severe" points down for km/L and up for every other indicator.
 */
export const alignThresholds = (
  descriptor: MetricDescriptor,
  thresholds: MetricThresholds,
  changed: keyof MetricThresholds
): MetricThresholds => {
  const inverted = descriptor.higherIsBetter
    ? thresholds.critical > thresholds.warn
    : thresholds.critical < thresholds.warn;

  if (!inverted) return thresholds;

  return changed === "warn"
    ? { warn: thresholds.warn, critical: thresholds.warn }
    : { warn: thresholds.critical, critical: thresholds.critical };
};

/** Zone of a value against the alert limits — the status shown under each gauge. */
export const zoneOf = (
  value: number | null | undefined,
  descriptor: MetricDescriptor,
  thresholds: MetricThresholds
): MetricZone | null => {
  if (!isFiniteNumber(value)) return null;

  const isWorse = (candidate: number, limit: number) => (descriptor.higherIsBetter ? candidate <= limit : candidate >= limit);

  if (isWorse(value, thresholds.critical)) return "critical";
  if (isWorse(value, thresholds.warn)) return "warning";

  return "ok";
};

/**
 * Band of the table heat map. The two "good" bands are the alert limits mirrored across the median,
 * so the scale stays symmetric whatever the customer sets. Returns `null` when the cell must stay
 * colorless: no value, or a vehicle with too few travels.
 */
export const heatShadeOf = (
  value: number | null | undefined,
  descriptor: MetricDescriptor,
  scale: MetricScale,
  thresholds: MetricThresholds
): HeatShade | null => {
  if (!isFiniteNumber(value)) return null;

  const isWorse = (candidate: number, limit: number) => (descriptor.higherIsBetter ? candidate <= limit : candidate >= limit);
  const isBetter = (candidate: number, limit: number) => (descriptor.higherIsBetter ? candidate >= limit : candidate <= limit);

  if (isWorse(value, thresholds.critical)) return "critical";
  if (isWorse(value, thresholds.warn)) return "warning";

  const mirroredCritical = (2 * scale.median) - thresholds.critical;
  const mirroredWarn = (2 * scale.median) - thresholds.warn;

  if (isBetter(value, mirroredCritical)) return "best";
  if (isBetter(value, mirroredWarn)) return "good";

  return "neutral";
};

export const isAttentionShade = (shade: HeatShade | null): boolean => shade === "warning" || shade === "critical";

/**
 * Orders the ranking worst first, which means ascending for km/L and descending for the others.
 * Vehicles without a value for the indicator are dropped.
 */
export const sortByWorstFirst = (
  vehicles: CanConsumptionDashboardVehicle[],
  descriptor: MetricDescriptor
): CanConsumptionDashboardVehicle[] => vehicles
  .filter((vehicle) => isFiniteNumber(vehicle[descriptor.key]))
  .sort((left, right) => {
    const leftValue = left[descriptor.key] as number;
    const rightValue = right[descriptor.key] as number;

    return descriptor.higherIsBetter ? leftValue - rightValue : rightValue - leftValue;
  });

/**
 * Falls back to the first indicator with data when the chosen one does not apply to the current
 * selection — a fleet of pumps, for instance, carries no m³.
 */
export const pickPrimaryMetric = (
  vehicles: CanConsumptionDashboardVehicle[],
  chosenKey: RankedMetricKey,
  minTravels: number = MIN_TRAVELS_FOR_RANKING
): MetricDescriptor => {
  const baseline = vehicles.filter((vehicle) => qualifiesForRanking(vehicle, minTravels));
  const hasData = (descriptor: MetricDescriptor) => baseline.some((vehicle) => isFiniteNumber(vehicle[descriptor.key]));
  const chosen = metricByKey(chosenKey);

  if (hasData(chosen)) return chosen;

  return RANKED_METRICS.find(hasData) ?? chosen;
};

/**
 * The daily bars measure m³ whenever any day carries volume, and fall back to liters otherwise —
 * a fleet of pumps transports no concrete, so a volume axis would be flat at zero for all of them.
 */
export const seriesUsesVolume = (days: CanConsumptionDashboardDay[]): boolean => (
  days.some((day) => isFiniteNumber(day.volumeM3) && day.volumeM3 > 0)
);

export interface DayBar {
  /** Height driver of the bar; `null` when the day has nothing to measure */
  value: number | null;
  /** `false` when there is no bar to draw — the column keeps its slot at height zero */
  hasBar: boolean;
  /** `false` only when the fleet did not run that day at all */
  hasOperation: boolean;
}

/**
 * Resolves one column of the daily chart.
 *
 * The series carries EVERY day of the period, so a day with no operation still gets a column: a
 * Sunday silently dropped turns a seven-day week into six bars and the gap goes unnoticed. A day
 * that DID operate but has no usable reading also draws nothing — the two cases differ only in what
 * the tooltip says, never in the axis.
 */
export const resolveDayBar = (day: CanConsumptionDashboardDay, barsUseVolume: boolean): DayBar => {
  const value = barsUseVolume ? day.volumeM3 : day.fuelLiters;

  return {
    value,
    hasBar: isFiniteNumber(value) && value > 0,
    hasOperation: day.travelsCount > 0
  };
};
